import { Text } from '@vkontakte/vkui';
import { isFeature } from 'Cloud/Application/FeaturesEs6';
import classNames from 'clsx';
import { xray } from 'lib/xray';
import React, { memo, useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { type NestleRegisterPetRequestParams, NestleRegisterPetApiCall } from 'reactApp/api/nestle/NestleRegisterPetApiCall';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { CardForm } from 'reactApp/sections/Nestle/CardForm/CardForm';
import { NestleFormWrapperContext } from 'reactApp/sections/Nestle/FormWrapper/FormWrapper.context';
import {
    nestleFormDataDefault,
    nestleFormErrorMessages,
    nestleFormErrorsDefault,
    nestleFormRequiredStep1,
    nestleFormRequiredStep4,
} from 'reactApp/sections/Nestle/FormWrapper/FormWrapper.data';
import { generatePetsCardsBlobData, NESTLE_BLOB_LIMIT } from 'reactApp/sections/Nestle/FormWrapper/FormWrapper.helpers';
import type { NestleFormData, NestleFormErrors } from 'reactApp/sections/Nestle/FormWrapper/FormWrapper.types';
import {
    sendNestleLandingAnalytics,
    sendNestleLandingSnitch,
    sendNestleLandingTmrGoal,
} from 'reactApp/sections/Nestle/NestleLanding.analytics';
import { PetsCard } from 'reactApp/sections/Nestle/PetsCard/PetsCard';
import { Animals, CardColorNumber } from 'reactApp/sections/Nestle/PetsCard/PetsCard.types';
import { TimelineMobile } from 'reactApp/sections/Nestle/TimelineMobile/TimelineMobile';
import { VerticalSteps } from 'reactApp/sections/Nestle/VerticalSteps/VerticalSteps';
import { validateEmail } from 'reactApp/utils/helpers';

import styles from './FormWrapper.css';

interface FormWrapperProps {
    onComplete?: () => void;
}

function downloadBlobImage(blob, filename) {
    const imageUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');

    downloadLink.href = imageUrl;
    downloadLink.download = filename;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    URL.revokeObjectURL(imageUrl);
}

export const FormWrapper = memo<FormWrapperProps>(({ onComplete }) => {
    const isMobile = useSelector(QuotaLandingSelectors.isMobile);

    const [formData, setFormData] = useState<NestleFormData>(nestleFormDataDefault);
    const [formErrors, setFormErrors] = useState<NestleFormErrors>(nestleFormErrorsDefault);

    const [formProcessing, setFormProcessing] = useState(false);
    const [submissionError, setSubmissionError] = useState('');

    const petCardRef = useRef<HTMLDivElement>(null);
    const verticalStepsRef = useRef<HTMLDivElement[]>([]);

    const validateFormData = useCallback(() => {
        const errors: NestleFormErrors = {
            photo: !formData.photo,
            nickname: !formData.nickname.trim(),
            nickname2: !formData.nickname2.trim(),
            animal: !formData.animal,
            animalOther: !formData.animalOther.trim() && formData.animal === Animals.other,
            breed: !formData.breed,
            date: !formData.date && !formData.dateUnknown,
            name: !formData.name.trim(),
            email: !validateEmail(formData.email),
            promo: !formData.promo,
            nestleData: !formData.nestleData,
            newsletters: !formData.newsletters,
            vkData: !formData.vkData,
        };

        setFormErrors((prev) => ({
            ...prev,
            ...errors,
        }));

        return errors;
    }, [formData, setFormErrors]);

    const scrollToStep = useCallback(
        (errors: NestleFormErrors) => {
            let step;

            if (nestleFormRequiredStep1.some((field) => errors[field])) {
                step = 0;
            } else if (errors.name || errors.email) {
                step = 2;
            } else if (nestleFormRequiredStep4.some((field) => errors[field])) {
                step = 3;
            }

            if (verticalStepsRef?.current?.[step]) {
                verticalStepsRef.current[step].scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        },
        [verticalStepsRef]
    );

    const onSubmit = useCallback(
        async function () {
            setFormProcessing(true);
            setSubmissionError('');

            const validationErrors = validateFormData();

            // Если были ошибки - скролим до первого шага с ошибками
            if (Object.values(validationErrors).some((error) => error)) {
                if (!isMobile) {
                    scrollToStep(validationErrors);
                }
                setFormProcessing(false);

                if (!isFeature('nestle-download')) {
                    return;
                }
            }

            try {
                if (petCardRef.current) {
                    const { mobileCard, desktopCard } = await generatePetsCardsBlobData(isMobile, petCardRef.current);

                    if (isFeature('nestle-download')) {
                        downloadBlobImage(desktopCard, 'desk.png');
                        downloadBlobImage(mobileCard, 'mobile.png');
                        return;
                    }

                    if (mobileCard && desktopCard) {
                        if (mobileCard.size + desktopCard.size > NESTLE_BLOB_LIMIT) {
                            xray.send(`nestle-blob-${desktopCard.size}`);
                            xray.send(`nestle-blob-m-${mobileCard.size}`);
                        }

                        const birth_date = formData.date ? Math.floor(formData.date.getTime() / 1000) : 0;
                        const metadata: NestleRegisterPetRequestParams['metadata'] = {
                            email: formData.email,
                            name: formData.name,
                            pet: {
                                alias: formData.nickname,
                                friends_alias: formData.nickname2,
                                type: formData.animal === Animals.other ? formData.animalOther : formData.animal,
                                breed: formData.breed,
                                is_sharable: formData.postInGroup,
                                birth_date,
                            },
                        };

                        const apiData = new FormData();

                        apiData.append('metadata', JSON.stringify(metadata));
                        apiData.append('mobile_file', mobileCard);
                        apiData.append('desktop_file', desktopCard);

                        await new NestleRegisterPetApiCall().makeRequest(apiData);

                        sendNestleLandingTmrGoal('click-participate-button');
                        sendNestleLandingAnalytics({
                            eventCategory: 'click',
                            action: 'click-participate-button',
                        });
                        sendNestleLandingSnitch('formSubmit', {
                            animal_name: formData.nickname,
                            gentle_name: formData.nickname2,
                            type: formData.animal === Animals.other ? formData.animalOther : formData.animal,
                            breed: formData.breed,
                            date: birth_date,
                            photo: 'yes',
                            colour: CardColorNumber[formData.cardColor],
                            owner_name: formData.name,
                            email: formData.email,
                            post_click: formData.postInGroup ? 'yes' : 'no',
                            rules_accept: 'yes',
                        });

                        onComplete?.();
                        xray.send('nestle-submit-success');
                    } else {
                        // Не удалось сгенерировать карточки.
                        xray.send('nestle-error-generate-card');
                        throw new Error('Не удалось сгенерировать карточки');
                    }
                } else {
                    // Отсустствует рефка карточки.
                    xray.send('nestle-error-no-card-ref');
                    throw new Error('Отсутствует рефка карточки');
                }
            } catch (err: any) {
                xray.send('nestle-error-api');

                if (err?.status === 413) {
                    xray.send(`nestle-error-blob-size-${isMobile ? 'mobile' : 'desktop'}`);
                }

                if (err?.status === 400 && nestleFormErrorMessages?.[err.error]) {
                    setSubmissionError(nestleFormErrorMessages?.[err.error]);
                } else {
                    setSubmissionError(nestleFormErrorMessages.COMMON);
                }
            } finally {
                setFormProcessing(false);
            }
        },
        [validateFormData, scrollToStep, petCardRef, isMobile, formData, setSubmissionError, setFormProcessing, onComplete]
    );

    return (
        <div
            className={classNames(styles.root, {
                [styles.rootMobile]: isMobile,
            })}
        >
            <NestleFormWrapperContext.Provider value={{ formData, setFormData, formErrors, setFormErrors }}>
                <PetsCard ref={petCardRef} />
                <CardForm onSubmit={onSubmit} isLoading={formProcessing} />
                {submissionError && <Text className={styles.errorText}>{submissionError}</Text>}
                {!isMobile ? <VerticalSteps ref={verticalStepsRef} /> : <TimelineMobile />}
            </NestleFormWrapperContext.Provider>
        </div>
    );
});

FormWrapper.displayName = 'FormWrapper';
